import { supabase } from '../lib/supabase';

class TrackingService {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.userCache = null;
    this.cacheExpiry = null;
  }

  generateSessionId() {
    return `${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`;
  }

  toCamelCase(obj) {
    if (!obj) return obj;
    const camelObj = {};
    for (const key in obj) {
      const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
      camelObj[camelKey] = obj?.[key];
    }
    return camelObj;
  }

  toSnakeCase(obj) {
    if (!obj) return obj;
    const snakeObj = {};
    for (const key in obj) {
      const snakeKey = key?.replace(/([A-Z])/g, '_$1')?.toLowerCase();
      snakeObj[snakeKey] = obj?.[key];
    }
    return snakeObj;
  }

  // Cache user data to reduce auth calls
  async getCachedUser() {
    const now = Date.now();
    // Cache for 5 minutes
    if (this.userCache && this.cacheExpiry && now < this.cacheExpiry) {
      return this.userCache;
    }

    try {
      const { data: { user } } = await this.retryAuthCall(() => supabase?.auth?.getUser());
      this.userCache = user;
      this.cacheExpiry = now + 300000; // 5 minutes
      return user;
    } catch (error) {
      console.warn('Failed to get user for tracking:', error?.message);
      return null;
    }
  }

  // Retry mechanism with exponential backoff
  async retryAuthCall(authFn, maxRetries = 3) {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await authFn();
      } catch (error) {
        // If it's a lock timeout error and we have retries left
        if (error?.message?.includes('lock') && error?.message?.includes('timed out') && attempt < maxRetries - 1) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 5000); // Max 5 seconds
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        throw error;
      }
    }
  }

  async logError(errorData) {
    try {
      const user = await this.getCachedUser();
      
      const insertData = {
        user_id: user?.id || null,
        error_type: errorData?.errorType || 'unknown',
        error_message: errorData?.errorMessage || 'No message',
        error_stack: errorData?.errorStack || null,
        page_url: errorData?.pageUrl || window?.location?.href,
        user_agent: navigator?.userAgent || null,
        browser_info: {
          language: navigator?.language,
          platform: navigator?.platform,
          vendor: navigator?.vendor
        },
        metadata: errorData?.metadata || {},
        resolved: false
      };

      const { data, error } = await supabase
        ?.from('error_logs')
        ?.insert(insertData)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data: this.toCamelCase(data), error: null };
    } catch (error) {
      // Silently fail - don't let tracking errors affect user experience
      console.warn('Error logging error (non-critical):', error?.message);
      return { data: null, error: error?.message };
    }
  }

  async logActivity(activityData) {
    try {
      const user = await this.getCachedUser();
      
      const insertData = {
        user_id: user?.id || null,
        session_id: this.sessionId,
        activity_type: activityData?.activityType,
        activity_description: activityData?.activityDescription,
        page_url: activityData?.pageUrl || window?.location?.href,
        metadata: activityData?.metadata || {}
      };

      const { data, error } = await supabase
        ?.from('user_activity')
        ?.insert(insertData)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data: this.toCamelCase(data), error: null };
    } catch (error) {
      // Silently fail - don't let tracking errors affect user experience
      console.warn('Error logging activity (non-critical):', error?.message);
      return { data: null, error: error?.message };
    }
  }

  async logFormEvent(formData) {
    try {
      const user = await this.getCachedUser();
      
      const insertData = {
        user_id: user?.id || null,
        session_id: this.sessionId,
        form_name: formData?.formName,
        form_step: formData?.formStep || null,
        event_type: formData?.eventType,
        field_name: formData?.fieldName || null,
        field_value: formData?.fieldValue || null,
        error_message: formData?.errorMessage || null,
        time_spent_seconds: formData?.timeSpentSeconds || 0,
        metadata: formData?.metadata || {}
      };

      const { data, error } = await supabase
        ?.from('form_analytics')
        ?.insert(insertData)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data: this.toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error logging form event:', error);
      return { data: null, error: error?.message };
    }
  }

  async logPerformance(performanceData) {
    try {
      const user = await this.getCachedUser();
      
      const insertData = {
        user_id: user?.id || null,
        session_id: this.sessionId,
        metric_type: performanceData?.metricType,
        metric_name: performanceData?.metricName,
        duration_ms: performanceData?.durationMs,
        page_url: performanceData?.pageUrl || window?.location?.href,
        success: performanceData?.success !== false,
        error_message: performanceData?.errorMessage || null,
        metadata: performanceData?.metadata || {}
      };

      const { data, error } = await supabase
        ?.from('performance_metrics')
        ?.insert(insertData)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data: this.toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error logging performance:', error);
      return { data: null, error: error?.message };
    }
  }

  async getErrorLogs(filters = {}) {
    try {
      let query = supabase
        ?.from('error_logs')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (filters?.resolved !== undefined) {
        query = query?.eq('resolved', filters?.resolved);
      }

      if (filters?.errorType) {
        query = query?.eq('error_type', filters?.errorType);
      }

      if (filters?.limit) {
        query = query?.limit(filters?.limit);
      } else {
        query = query?.limit(100);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: data?.map(this.toCamelCase), error: null };
    } catch (error) {
      console.error('Error fetching error logs:', error);
      return { data: null, error: error?.message };
    }
  }

  async getUserActivity(filters = {}) {
    try {
      let query = supabase
        ?.from('user_activity')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (filters?.userId) {
        query = query?.eq('user_id', filters?.userId);
      }

      if (filters?.activityType) {
        query = query?.eq('activity_type', filters?.activityType);
      }

      if (filters?.limit) {
        query = query?.limit(filters?.limit);
      } else {
        query = query?.limit(100);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: data?.map(this.toCamelCase), error: null };
    } catch (error) {
      console.error('Error fetching user activity:', error);
      return { data: null, error: error?.message };
    }
  }

  async getFormAnalytics(filters = {}) {
    try {
      let query = supabase
        ?.from('form_analytics')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (filters?.formName) {
        query = query?.eq('form_name', filters?.formName);
      }

      if (filters?.eventType) {
        query = query?.eq('event_type', filters?.eventType);
      }

      if (filters?.limit) {
        query = query?.limit(filters?.limit);
      } else {
        query = query?.limit(100);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: data?.map(this.toCamelCase), error: null };
    } catch (error) {
      console.error('Error fetching form analytics:', error);
      return { data: null, error: error?.message };
    }
  }

  async getPerformanceMetrics(filters = {}) {
    try {
      let query = supabase
        ?.from('performance_metrics')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (filters?.metricType) {
        query = query?.eq('metric_type', filters?.metricType);
      }

      if (filters?.success !== undefined) {
        query = query?.eq('success', filters?.success);
      }

      if (filters?.limit) {
        query = query?.limit(filters?.limit);
      } else {
        query = query?.limit(100);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data: data?.map(this.toCamelCase), error: null };
    } catch (error) {
      console.error('Error fetching performance metrics:', error);
      return { data: null, error: error?.message };
    }
  }

  async getFormAbandonmentSummary() {
    try {
      const { data, error } = await supabase
        ?.from('form_abandonment_summary')
        ?.select('*')
        ?.order('abandonment_rate', { ascending: false });

      if (error) throw error;
      return { data: data?.map(this.toCamelCase), error: null };
    } catch (error) {
      console.error('Error fetching form abandonment summary:', error);
      return { data: null, error: error?.message };
    }
  }

  async markErrorResolved(errorId, resolvedBy) {
    try {
      const { data, error } = await supabase
        ?.from('error_logs')
        ?.update({
          resolved: true,
          resolved_at: new Date()?.toISOString(),
          resolved_by: resolvedBy
        })
        ?.eq('id', errorId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data: this.toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error marking error as resolved:', error);
      return { data: null, error: error?.message };
    }
  }
}

export const trackingService = new TrackingService();